import type { RendererProps } from "../core/element-registry"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { Spinner } from "../components/ui/spinner"
import { cn } from "../lib/utils"

// Size axis maps to Tailwind size-* utility per shadcn convention.
// default-theme.json matrix: availableSizes ["default","sm","lg"].
const SIZE_CLASS: Record<string, string> = {
  default: "size-4",
  sm: "size-3",
  lg: "size-6",
}

export function LoaderRenderer({ element }: RendererProps) {
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("loader")
  const resolvedVariant = element.variant ?? atomDefaults.defaultVariant ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  const sizeClass = SIZE_CLASS[resolvedSize] ?? SIZE_CLASS.default

  // Wrap the Spinner in an inline-flex span so the data-atom-type marker is
  // always in the DOM, even when the underlying lucide icon lookup returns
  // null (e.g. in test environments that don't ship the full icons map).
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cn("inline-flex items-center", element.className)}
      data-testid={`loader-${element.id}`}
      data-atom-type="loader"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    >
      <Spinner className={sizeClass} />
    </span>
  )
}
